import { useCallback, useEffect, useRef } from 'react';

import { useAuthContext } from './auth.provider';
import { useLocale } from './locale.provider';
import { useUserContext, useUserDispatch } from './user/user.provider';
import { updateUser } from './user/user.actions';
import { normalizeLocale, SupportedLocale } from '../i18n/locales';
import { patchUser } from '../services/user.service';
import { captureException } from '../analytics/sentry';

/**
 * Bridges the device-local locale (LocaleProvider) with the user's
 * server-persisted preference (`preferredLocale` on the user doc) so language
 * follows the user across devices.
 *
 * LocaleProvider sits above AuthProvider/UserProvider in the tree and therefore
 * cannot read the authenticated user itself. This component renders inside
 * UserProvider, consumes both contexts, and renders nothing.
 *
 * Reconciliation policy (runs once per signed-in user):
 *   1. `?lang`/`?locale` link override wins and is pushed up as the new
 *      preference (mirrors the existing "querystring always wins" rule).
 *   2. Otherwise the server value is the cross-device source of truth and is
 *      applied to the device + UI.
 *   3. If the server has no preference yet, it is seeded from the current
 *      device locale.
 * After reconciliation, any locale change from the toggle is persisted up.
 */
export const LocaleUserSync = (): null => {
  const { locale, setLocale, source } = useLocale();
  const { user } = useUserContext();
  const userDispatch = useUserDispatch();
  const { firebaseUser } = useAuthContext();

  // The locale we know the server already holds. Guards the change-watcher
  // below from echoing a server-driven hydration back up as a "user edit".
  const serverLocaleRef = useRef<SupportedLocale | null>(null);
  // uid we've already reconciled, so we only reconcile once per sign-in.
  const reconciledUidRef = useRef<string | null>(null);

  const persist = useCallback(
    async (next: SupportedLocale) => {
      try {
        await patchUser({ preferredLocale: next }, firebaseUser);
        userDispatch(updateUser({ preferredLocale: next }));
      } catch (e) {
        captureException(e);
      }
    },
    [firebaseUser, userDispatch],
  );

  // Reconcile once when a signed-in user is hydrated.
  useEffect(() => {
    if (!user || !firebaseUser || firebaseUser.isAnonymous) return;
    if (reconciledUidRef.current === firebaseUser.uid) return;
    reconciledUidRef.current = firebaseUser.uid;

    // normalizeLocale coerces a legacy `es` preference to `es-PR`.
    const serverLocale = normalizeLocale(user.preferredLocale) ?? null;

    if (source === 'query' || !serverLocale) {
      // Querystring override, or server has nothing yet → seed the server.
      serverLocaleRef.current = locale;
      void persist(locale);
    } else if (serverLocale !== locale) {
      // Adopt the server preference across devices.
      serverLocaleRef.current = serverLocale;
      void setLocale(serverLocale);
    } else {
      serverLocaleRef.current = serverLocale;
    }
  }, [user, firebaseUser, source, locale, setLocale, persist]);

  // Persist subsequent toggle changes up to the server.
  useEffect(() => {
    if (!user || !firebaseUser || firebaseUser.isAnonymous) return;
    if (serverLocaleRef.current === null) return; // not reconciled yet
    if (locale === serverLocaleRef.current) return; // no change / server-driven
    serverLocaleRef.current = locale;
    void persist(locale);
  }, [locale, user, firebaseUser, persist]);

  // Reset when the user signs out so the next sign-in reconciles cleanly.
  useEffect(() => {
    if (!firebaseUser || firebaseUser.isAnonymous) {
      reconciledUidRef.current = null;
      serverLocaleRef.current = null;
    }
  }, [firebaseUser]);

  return null;
};
